"use client";

import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { CheckCircle2, CircleAlert } from "lucide-react";
import { cn } from "./cn";

export type ToastVariant = "default" | "success" | "error";

export type ToastMessage = {
  id: string;
  title: string;
  description?: string;
  /** Visual status — icon + accent. Default is neutral. */
  variant?: ToastVariant;
  actionLabel?: string;
  onAction?: () => void;
};

type ToastContextValue = {
  toasts: ToastMessage[];
  push: (toast: Omit<ToastMessage, "id">) => void;
  dismiss: (id: string) => void;
};

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

function ToastStatusIcon({ variant }: { variant: ToastVariant }) {
  if (variant === "success") {
    return (
      <CheckCircle2
        className="mt-0.5 h-4 w-4 shrink-0 text-success"
        strokeWidth={1.75}
        aria-hidden
      />
    );
  }
  if (variant === "error") {
    return (
      <CircleAlert
        className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
        strokeWidth={1.75}
        aria-hidden
      />
    );
  }
  return null;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);

  const push = React.useCallback((toast: Omit<ToastMessage, "id">) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev.slice(-2), { ...toast, id }]);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, push, dismiss }}>
      <ToastPrimitive.Provider swipeDirection="right" duration={4000}>
        {children}
        <div aria-live="polite" aria-relevant="additions" className="contents">
          {toasts.map((toast) => {
            const variant = toast.variant ?? "default";
            return (
              <ToastPrimitive.Root
                key={toast.id}
                open
                onOpenChange={(open) => {
                  if (!open) dismiss(toast.id);
                }}
                className={cn(
                  "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-card p-4 shadow-lg",
                  "data-[state=open]:animate-fade-in",
                  variant === "success" && "border-success/30",
                  variant === "error" && "border-destructive/30",
                  variant === "default" && "border-border",
                )}
              >
                <ToastStatusIcon variant={variant} />
                <div className="min-w-0 flex-1 space-y-1">
                  <ToastPrimitive.Title className="text-sm font-medium">
                    {toast.title}
                  </ToastPrimitive.Title>
                  {toast.description ? (
                    <ToastPrimitive.Description className="text-sm text-muted-foreground">
                      {toast.description}
                    </ToastPrimitive.Description>
                  ) : null}
                </div>
                {toast.actionLabel && toast.onAction ? (
                  <ToastPrimitive.Action
                    altText={toast.actionLabel}
                    className="rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={toast.onAction}
                  >
                    {toast.actionLabel}
                  </ToastPrimitive.Action>
                ) : null}
                <ToastPrimitive.Close className="text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  ×
                </ToastPrimitive.Close>
              </ToastPrimitive.Root>
            );
          })}
        </div>
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[100] flex max-h-screen w-full max-w-sm flex-col gap-2 outline-none md:bottom-auto md:top-4" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
